import { DestroyRef, inject, Injectable, signal, type Signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';

/**
 * Route declaration for the filter bar's per-route opt-out (R-011), following
 * the `BreadcrumbRouteData` pattern rather than inventing a second mechanism.
 *
 * The default is **shown**, and opting out is explicit. An opt-in default would
 * silently drop the bar from AITH-45–48's pages the moment they are added — the
 * exact failure mode the spec leaves open by deferring configuration-style
 * screens to the consuming stories.
 */
export interface FilterBarRouteData {
  /** Present only on a route that must NOT show the bar (`sign-in`, `**`). */
  readonly hidden?: true;

  /**
   * Selectors this destination removes from an otherwise-visible bar (AITH-45 U3).
   *
   * The Executive Overview is the motivating case and shows why this is not the same thing as the
   * FR-016 "unavailable" state. For an internal caller with **no client selected**, app/model/
   * provider have no obtainable option values at all — `/dashboard/usage` rejects a staff call that
   * omits `clientCode`, so there is no legal way to ask for cross-client observed values (AITH-44
   * R-003). A cross-client page would therefore render three permanently inert controls, on the one
   * destination where a client is least likely to be selected.
   *
   * Removing them is an obligation-6 treatment ("declared not-applicable"), not a workaround: the
   * absence IS the declaration, and the page states it in prose alongside.
   *
   * > **Amended 2026-09-19 (GH-34 §1, `01-filters-decisions.md` D-2/D-3).** The argument above is
   * > sound and its conclusion no longer applies to app/model/provider. It reasons about option
   * > *values*, and {@link disabledDimensions} needs none: those three are now **shown disabled**
   * > on every dashboard page, because `rollup_daily` genuinely carries them (`RollupKey.RowKey`,
   * > `ProviderRollupKey`) and a rollup-first read would lift exactly the R-003 constraint this
   * > docblock describes. What is hidden here now is `user`/`status` — and `client` where it does
   * > not apply — which no data-source migration revives. See {@link disabledDimensions}.
   *
   * > **Amended 2026-10-29 (GH-164 stage 1, aith-api#164 decisions D-02).** Not every dashboard page
   * > any more: `/home`, `/clients/:clientCode` and `/usage` declare app/model/provider here, because
   * > no figure on those pages can follow them and a disabled control would promise that one will.
   */
  readonly hiddenDimensions?: readonly string[];

  /**
   * Selectors this destination renders but does not let the reader operate (GH-34 §1, D-3/D-5).
   *
   * The **seventh** treatment in the AITH-44/45/46/47/48 contract vocabulary:
   *
   * > **Disabled** — The selector renders and cannot be operated. It shows the view's actual scope
   * > as its value, and its status line states why. The visible control **is** the declaration.
   *
   * It states a different fact from {@link hiddenDimensions}, which is why there are two and not
   * one. *Hidden* says "this does not apply to this view" — structural, and no migration touches
   * it. *Disabled* says "this exists and is not switched on here yet" — true only for the three
   * dimensions a rollup-first read could revive, which is what makes the greyed control a promise
   * the platform can keep. Never extend this to `user` (a recorded privacy refusal — AITH-44 spec
   * gap resolution C2, closed fail-closed by AITH-45) or `status` (absent from `rollup_daily`
   * entirely): a disabled control there would advertise a capability that is not coming.
   *
   * A disabled control is out of the tab order, so its reason cannot be announced on focus. It
   * lives in the always-rendered `filter-status` line instead (D-8 R1), which is **visible,
   * document-order page text** — that is what makes it discoverable on a cold load. The line also
   * carries `role="status"`, which announces it when a route change alters the treatment while the
   * bar stays mounted; see `searchable-select.component.ts` for why that is an addition rather than
   * the mechanism.
   *
   * Never use the word *ignored* for either treatment: that is the one obligation 6 prohibits, and
   * `AITH-47/plans/D1` greps the contracts directory for it expecting no hits.
   */
  readonly disabledDimensions?: readonly (string | DisabledDimension)[];
}

/**
 * GH-164 S2-U1 (decisions D-11): a disabled dimension with its OWN reason, for a view where the
 * default "Not available here yet" is not the truth — for example a dimension that cannot combine
 * with another selection. The plain string form keeps the default reason, so every existing
 * declaration is unchanged.
 */
export interface DisabledDimension {
  readonly dimension: string;
  readonly reason: string;
}

export const FILTER_BAR_ROUTE_DATA_KEY = 'filterBar';

/**
 * Whether the filter bar shows on the active destination.
 *
 * Resolved from the activated-route tree on every `NavigationEnd`, exactly as
 * `BreadcrumbService` resolves its segments — one mechanism, one place. The
 * shell renders the bar under `@if` on this, never CSS-hiding it (Art. XI-C).
 *
 * This decides *presentation only*: the shared state is root-provided, so an
 * opted-out route neither resets nor re-reads it (R-011).
 *
 * The route-data default is **shown**, but only for a *resolved* destination —
 * see {@link visibleState}.
 */
@Injectable({ providedIn: 'root' })
export class FilterBarVisibilityService {
  private readonly router = inject(Router);

  /**
   * Starts **hidden**, because "shown by default" is a statement about a
   * resolved destination and before the first navigation there is none:
   * `routerState.root` has no `firstChild`, so {@link recompute} finds no
   * `filterBar.hidden` anywhere and would report *shown*. Initial navigation is
   * non-blocking and every route is `loadComponent`, so the shell paints before
   * the first `NavigationEnd` — a cold load of `sign-in` or a 404 would render
   * the dashboard controls until the lazy chunk resolved, which on a slow
   * connection is not a single frame. Seeding the absent state instead is what
   * `BreadcrumbService` does with its empty segment list, and it makes the bar
   * appear with the content it belongs to.
   */
  private readonly visibleState = signal(false);
  private readonly hiddenDimensionsState = signal<ReadonlySet<string>>(new Set());
  private readonly disabledDimensionsState = signal<ReadonlySet<string>>(new Set());
  private readonly disabledReasonsState = signal<ReadonlyMap<string, string>>(new Map());

  readonly visible: Signal<boolean> = this.visibleState.asReadonly();

  /** Selectors the active destination removes. Empty on every route that declares none. */
  readonly hiddenDimensions: Signal<ReadonlySet<string>> = this.hiddenDimensionsState.asReadonly();

  /**
   * Selectors the active destination renders inert. Empty on every route that declares none.
   *
   * A dimension in BOTH sets is hidden: absence wins, because a control that is not rendered
   * cannot also be shown disabled. The bar reads them in that order.
   */
  readonly disabledDimensions: Signal<ReadonlySet<string>> =
    this.disabledDimensionsState.asReadonly();

  /**
   * GH-164 D-11: the reasons declared with {@link DisabledDimension}, by dimension. A disabled
   * dimension absent from this map shows the control's default reason.
   */
  readonly disabledReasons: Signal<ReadonlyMap<string, string>> = this.disabledReasonsState.asReadonly();

  constructor() {
    // Only meaningful once a destination exists. `Router.navigated` is the same
    // guard `FilterUrlProjection.readOnce()` reads for the same reason, and it
    // keeps a host constructed *after* the first navigation correct rather than
    // hidden until the next one.
    if (this.router.navigated) this.recompute();
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(inject(DestroyRef)),
      )
      .subscribe(() => this.recompute());
  }

  private recompute(): void {
    let route: ActivatedRoute | null = this.router.routerState.root;
    let hidden = false;
    const hiddenDimensions = new Set<string>();
    const disabledDimensions = new Set<string>();
    const disabledReasons = new Map<string, string>();
    while (route) {
      const data = route.routeConfig?.data?.[FILTER_BAR_ROUTE_DATA_KEY] as
        | FilterBarRouteData
        | undefined;
      if (data?.hidden === true) hidden = true;
      // Accumulated down the tree, like `hidden`: a parent's removal is not undone by a child.
      for (const dimension of data?.hiddenDimensions ?? []) hiddenDimensions.add(dimension);
      // Identically, and for the same reason: a parent's declaration is a floor, not a default.
      for (const entry of data?.disabledDimensions ?? []) {
        if (typeof entry === 'string') {
          disabledDimensions.add(entry);
        } else {
          disabledDimensions.add(entry.dimension);
          // The deepest declaration's reason wins: it describes the view the reader is on.
          disabledReasons.set(entry.dimension, entry.reason);
        }
      }
      route = route.firstChild;
    }
    this.visibleState.set(!hidden);
    this.hiddenDimensionsState.set(hiddenDimensions);
    this.disabledDimensionsState.set(disabledDimensions);
    this.disabledReasonsState.set(disabledReasons);
  }
}
